import { keepPreviousData, useQuery } from "@tanstack/vue-query";
import isDeepEqual from "fast-deep-equal";
import { computed, type MaybeRefOrGetter, toValue } from "vue";

import { debouncedRef } from "@/lib/composables/debounced-ref";
import { useStablePending } from "@/lib/composables/stable-pending";
import type { Newsletter } from "@/types/newsletter";

import { useEmailDetailQuery } from "../emails/detail";
import { useLatestPublishedEmail } from "../emails/list";
import { useNewsletterId, useXsrfHeaders } from "../utils";
import { fetchRender } from "./helpers";
import { sampleEmail } from "./sample";
import type {
  NewsletterDraftMedium,
  PreviewSource,
  PreviewSourceFactory,
} from "./types";

interface NewsletterDraftInput {
  newsletter: Partial<Newsletter>;
  // Picks which sent email's substitution context to render against. When
  // omitted, falls back to the most recent published email; when neither
  // is available, sampleEmail provides static placeholder text.
  selectedEmailId?: string;
  // Overrides the reference email's `template` for this render. The custom
  // template editor sets "naked" so the preview renders the custom HTML even
  // when the reference email has an explicit built-in template.
  template?: string;
}

export const useNewsletterDraftPreview = <C extends NewsletterDraftMedium>(
  input: MaybeRefOrGetter<NewsletterDraftInput>,
  options: {
    context: C;
    subscriberId?: MaybeRefOrGetter<string | undefined>;
    enabled?: MaybeRefOrGetter<boolean>;
  }
): PreviewSource => {
  const newsletterId = useNewsletterId();
  const xsrfHeaders = useXsrfHeaders();

  const enabled = computed(() => toValue(options.enabled) ?? true);

  const selectedEmailId = computed(() => toValue(input).selectedEmailId);

  // Each upstream query is gated so only the relevant one subscribes.
  const latestEmail = useLatestPublishedEmail({
    enabled: computed(
      () => enabled.value && selectedEmailId.value === undefined
    ),
  });

  const selectedEmailQuery = useEmailDetailQuery({
    id: selectedEmailId,
    enabled: computed(
      () => enabled.value && selectedEmailId.value !== undefined
    ),
  });

  const referenceEmail = computed(() => {
    return selectedEmailId.value !== undefined
      ? selectedEmailQuery.data.value
      : latestEmail.value;
  });

  // Debounce newsletter content; selector + subscriber changes flow through.
  const debouncedNewsletter = debouncedRef(
    computed<Partial<Newsletter>>((prev) => {
      const next = toValue(input).newsletter;
      if (prev !== undefined && isDeepEqual(prev, next)) {
        return prev;
      }
      return next;
    }),
    500,
    { enabled }
  );

  const queryParams = computed(() => ({
    newsletter: debouncedNewsletter.value,
    referenceEmailId: referenceEmail.value?.id,
    subscriberId: toValue(options.subscriberId),
    template: toValue(input).template,
    // not sure why useQuery() would complain otherwise
    context: options.context as NewsletterDraftMedium,
  }));

  // Block the render until the upstream email query has resolved (success
  // or error) — otherwise we'd fire with no substitution context and
  // immediately invalidate.
  const isReady = computed(() => {
    if (!enabled.value) return false;
    if (selectedEmailId.value !== undefined) {
      return (
        selectedEmailQuery.isSuccess.value || selectedEmailQuery.isError.value
      );
    }
    return true;
  });

  const query = useQuery({
    queryKey: [newsletterId, "newsletter-draft-preview", queryParams],
    enabled: isReady,
    gcTime: 10_000,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    queryFn({ signal }) {
      const params = queryParams.value;
      const ref = referenceEmail.value;
      // Backend's `text` field doubles as either raw markdown or an email
      // ID to look up — preserved from the original useDraftPreviewQuery.
      const emailFields = ref
        ? { subject: ref.subject, text: ref.id, id: ref.id }
        : { subject: sampleEmail.subject, text: sampleEmail.body };

      return fetchRender(
        {
          newsletter: params.newsletter,
          subscriber_id: params.subscriberId,
          // Omitted from the JSON body when undefined; overrides the reference
          // email's template (e.g. "naked" for the custom-template editor).
          template: params.template,
          context: params.context as C,
          ...emailFields,
        },
        { signal, headers: xsrfHeaders.value }
      );
    },
  });

  const isStableFetching = useStablePending(query.isFetching);

  return {
    html: query.data,
    error: query.error,
    pending: computed(() => query.isLoading.value || isStableFetching.value),
  };
};

export const newsletterDraftPreviewSource = <C extends NewsletterDraftMedium>(
  context: C,
  input: MaybeRefOrGetter<NewsletterDraftInput>,
  options: { subscriberId?: MaybeRefOrGetter<string | undefined> } = {}
): PreviewSourceFactory<`newsletter_draft:${C}`> => ({
  key: `newsletter_draft:${context}`,
  create: ({ enabled }) => {
    return useNewsletterDraftPreview(input, {
      context,
      subscriberId: options.subscriberId,
      enabled,
    });
  },
});
